import { IonContent, IonRefresher, IonRefresherContent } from '@ionic/react';
import { cn } from '@up2stay/up2promo-ui';
import type { CSSProperties, ReactNode } from 'react';

import {
  runPullToRefresh,
  shouldEnableShellPullToRefresh,
} from '@/core/lib/shell-pull-to-refresh';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';
import { shellUsesMobileTabBar } from '@/core/utils/shell-chrome';

interface AppPageContentProps {
  children: ReactNode;
  className?: string;
  fullscreen?: boolean;
  scrollY?: boolean;
  style?: CSSProperties;
  /** Shell tab pages include bottom nav inset; standalone pages only use safe areas. */
  inset?: 'shell' | 'standalone';
  /**
   * Capacitor-only pull-to-refresh (DEV-441). Ignored in the browser (any
   * width) and when `scrollY` is false. Handler should invalidate query keys.
   */
  onRefresh?: () => void | Promise<void>;
  [key: string]: unknown;
}

function getShellContentSafeAreaStyle(
  inset: 'shell' | 'standalone',
  showTabBarClearance: boolean,
  scrollY: boolean,
): CSSProperties {
  const tabBarPad = 'var(--shell-mobile-tab-bar-clearance)';
  const safePad = 'env(safe-area-inset-bottom, 0px)';
  const keyboardPad = 'var(--keyboard-inset-height, 0px)';
  const shellPad =
    inset === 'shell' && showTabBarClearance ? tabBarPad : safePad;

  return {
    '--padding-start': 'env(safe-area-inset-left, 0px)',
    '--padding-end': 'env(safe-area-inset-right, 0px)',
    '--padding-top':
      inset === 'standalone' ? 'env(safe-area-inset-top, 0px)' : '0px',
    // Non-scrolling pages (wizards) must not shrink h-full by the keyboard
    // height — that leaves a dead band above the keys. Inner scrollers pad.
    '--padding-bottom': scrollY
      ? `calc(${shellPad} + ${keyboardPad})`
      : shellPad,
  } as CSSProperties;
}

/**
 * iv-ac style page body: plain `IonContent` with safe-area via Ionic padding vars.
 */
export function AppPageContent({
  children,
  className,
  fullscreen = false,
  scrollY = true,
  style,
  inset = 'shell',
  onRefresh,
  ...props
}: AppPageContentProps) {
  // Cap shell pages always pad for the floating bar (always painted on Cap).
  // Bootstrap-only: `html[data-shell-tab-bar-chrome=collapsed]` shrinks the
  // clearance token to safe-area until the main bar mounts.
  const showTabBarClearance = shellUsesMobileTabBar();
  const showPullToRefresh = shouldEnableShellPullToRefresh({
    isCapacitorNative: isCapacitorNative(),
    hasHandler: typeof onRefresh === 'function',
    scrollY,
  });

  return (
    <IonContent
      fullscreen={fullscreen}
      scrollY={scrollY}
      // Short pages still need bounce so the pull gesture can start.
      forceOverscroll={showPullToRefresh || undefined}
      // Needed so shell chrome can listen for bubbling `ionScroll` (tab bar compact).
      scrollEvents={showTabBarClearance || undefined}
      className={cn('main-content shell-page-content', className)}
      style={{
        ...getShellContentSafeAreaStyle(inset, showTabBarClearance, scrollY),
        ...style,
      }}
      {...props}
    >
      {showPullToRefresh && onRefresh ? (
        <IonRefresher
          slot="fixed"
          data-slot="shell-pull-to-refresh"
          onIonRefresh={(event) => {
            void runPullToRefresh(event, onRefresh);
          }}
        >
          {/* pullingIcon = JS refresher (native ticks hide under the status-bar blur). */}
          <IonRefresherContent
            pullingIcon="circular"
            refreshingSpinner="crescent"
          />
        </IonRefresher>
      ) : null}
      {children}
    </IonContent>
  );
}
